/**
 * 🪵 The shape of a piece of driftwood lying on the beach — **D105**.
 *
 * ## 🔴 Why this is not a cube, and not in the world mesh
 *
 * 🎯 **haugur, 2026-09-02, shown the alternative:** *"4 or 10 cubes in a row on
 * a beach is just funny looking."* He is right, and the engine already has the
 * way out: `mesher.ts` can only emit **axis-aligned faces of a box scaled in
 * Y** — *"that is the whole vocabulary it has"* — so anything that is not a box
 * is skipped there and drawn beside the terrain instead. **`kyndill` is the
 * precedent and it is two days old.**
 *
 * ⚠️ **So the piece on the beach and the block in your pack are not the same
 * thing.** 🎯 **haugur:** *"it can be a different element all together, then
 * after you pick it up it changes to a cube."* The lying piece is drawn from
 * here; the thing you carry and build with is an ordinary block.
 *
 * ## ✅ Everything about a piece comes from WHERE IT IS
 *
 * Length, thickness, taper, which way it points, how it leans, how it bows and
 * how lumpy it is are all read out of a hash of the world position. **No random
 * numbers and nothing stored.**
 *
 * 🔴 **This is `berries.ts`'s rule and the reason is the same one written
 * there:** *"a random answer cannot be tested and cannot be reproduced"*, and
 * **D2** means haugur cannot read the code to find out why a piece looked
 * wrong. A hash also means the beach looks the same every time you walk back to
 * it, which is what makes a place a place.
 *
 * ## ⚠️ The winding is DERIVED, never listed
 *
 * `torchmesh.ts` paid for this on 2026-08-31: four faces were written out by
 * hand, **two of them were wound backwards and were back-face culled**, and
 * haugur saw a torch as *"two thin dark lines"*. Here every face's normal is
 * computed **from its own corners**, so a normal cannot disagree with the
 * triangle it belongs to. 🔴 **And `06_Traps.md` warns that a preview which
 * draws every triangle cannot show a winding fault and will be believed** — so
 * the preview culls, and a test recomputes normals from corners.
 *
 * ## What this module deliberately does NOT do
 *
 * No `three` import, no materials, no colour. It returns plain arrays, so it
 * tests as arithmetic without a browser, a GPU or a world — the same shape as
 * `edits.ts`. The mesh wrapper belongs beside the renderer.
 */

/** How many sides the prism has. Six reads round enough at 10 m and is cheap. */
export const SIDES = 6;

/** How many segments along the piece. Three gives two bends — enough to bow. */
export const SEGMENTS = 3;

/** One piece, as flat-shaded triangles. */
export interface DriftGeometry {
  /** `x,y,z` per vertex. */
  readonly positions: Float32Array;
  /** One normal per vertex, flat-shaded. */
  readonly normals: Float32Array;
  /** Triangle indices. */
  readonly indices: Uint32Array;
}

/** Where a piece lies, in world metres — the cell it belongs to. */
export interface DriftAt {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * 🔴 **How many pieces are in one cell, and why it is never one.**
 *
 * ⚠️ **One piece per cell was built first and it was wrong.** `Design/samples/53`
 * showed it: every piece parked neatly in the middle of its own square metre
 * with gravel all round it, which reads as **sticks laid out on a lawn**. Both
 * of haugur's references — his own photograph of an Icelandic rekaviður shore
 * and the Stranded Deep shot — are **piles**, where pieces cross and lie over
 * each other. **The crossing is what makes it read as driftwood.**
 *
 * ✅ So a cell holds a small heap, and the pieces inside it are free to overlap
 * each other. They stay inside their own metre, so what you see is still what
 * you can aim at.
 */
export const MIN_PIECES = 2;
export const MAX_PIECES = 5;

/** How many pieces lie in this cell. */
export function driftPiecesAt(at: DriftAt): number {
  return MIN_PIECES + (hash(at.x, at.y, at.z, 99) % (MAX_PIECES - MIN_PIECES + 1));
}

/**
 * The cheap integer scramble, lifted from `berries.ts`.
 *
 * ⚠️ **It needs to be uncorrelated with the grid, not cryptographic.** `salt`
 * separates the several questions asked of one position, so that a piece's
 * length and its angle are not the same number wearing two hats — which is how
 * you get a beach where every long piece points the same way.
 */
function hash(x: number, y: number, z: number, salt: number): number {
  let h = Math.trunc(x) * 0x1f1f_1f1f;
  h = (h ^ (Math.trunc(y) * 0x85eb_ca6b)) | 0;
  h = (h ^ (Math.trunc(z) * 0xc2b2_ae35)) | 0;
  h = (h ^ (salt * 0x27d4_eb2d)) | 0;
  h = (h ^ (h >>> 15)) | 0;
  return h >>> 0;
}

/** A hashed number in `[lo, hi)`. */
function pick(at: DriftAt, salt: number, lo: number, hi: number): number {
  return lo + (hash(at.x, at.y, at.z, salt) / 0x1_0000_0000) * (hi - lo);
}

/**
 * How a piece is proportioned.
 *
 * ⚠️ **Every number here is a PLAYTEST number**, the same warning D85, D86,
 * D103 and D104 carry. They are chosen so a piece sits inside its own cell:
 * one block is one metre (**D8**), and a piece longer than that would overhang
 * cells it does not own — you would see timber you cannot aim at, which reads
 * as broken rather than as scenery.
 */
export const SHAPE = Object.freeze({
  /**
   * Length along the axis, in metres.
   *
   * 🔴 **RAISED AGAIN, because haugur lifted the constraint that held it down.**
   * 🎯 **2026-09-02:** *"yes let them overhang the cell."* Asked directly whether
   * a piece may reach past its own metre — which looks far more like his
   * photographs of a rekaviður shore — and told what it costs: **you can see
   * timber at the ends that you cannot aim at.** He took it.
   *
   * ⚠️ **So the ceiling is no longer set by the cell.** It was 0.72 only because
   * a piece that may not overhang has nowhere to sit but the middle, and a heap
   * of centred pieces crosses at one point instead of scattering.
   *
   * ⚠️ **A CORRECTION, and it matters because the wrong version was written down
   * first.** This note used to say those centred heaps *"read as a sheaf of
   * wheat standing up"*. **They do not, and nothing here ever stood up.** The
   * isometric preview turns a piece lying flat along the view's own +x +z
   * diagonal into a **vertical line on screen**, and four of them into a sheaf.
   * ✅ `Design/samples/53` now draws every cell from straight down as well, and
   * the plan view shows a flat scatter. 👉 **The camera was the fault. The
   * shorter length is still right, for the room argument above.**
   *
   * ⚠️ **The alternative was to let pieces OVERHANG the cell**, which looks more
   * like haugur's photographs and costs you timber you can see and cannot aim
   * at. **That is his open question, not mine to settle.**
   */
  minLength: 0.5,
  maxLength: 1.5,
  /**
   * 🔴 **How slender a piece is — its length divided by its thickness.**
   *
   * ⚠️ **Thickness used to be picked independently of length, and that was the
   * bug `Design/samples/48` kept showing.** The two rolled separately, so a cell
   * could get a piece **0.55 m long and 0.23 m thick** — an aspect of 2.4 to 1,
   * which is not a log, it is a rock. Four of those overlapping read as **a
   * stump or a boulder**, and no amount of spreading them fixed it.
   *
   * ✅ Deriving thickness from length keeps every piece slender however long it
   * is, so a short piece is a short *stick* rather than a lump.
   */
  minAspect: 6,
  maxAspect: 13,
  /** What the thin end's radius is, as a share of the thick end's. */
  minTaper: 0.55,
  maxTaper: 0.9,
  /** How far the piece bows off a straight line, as a share of its length. */
  maxBow: 0.09,
  /** How far it leans out of horizontal, in radians. Driftwood lies down. */
  maxTilt: 0.22,
  /** How much each ring's radius wobbles, as a share. This is what stops it being a dowel. */
  maxWobble: 0.28,
  /**
   * 🔴 **How far a piece may sit from the middle of its cell is DERIVED, not
   * listed here** — see `room` in {@link buildDriftPiece}. It is whatever is
   * left inside the metre once the piece's own reach along that axis is taken
   * out, so a piece can never overhang a cell it does not own.
   *
   * ⚠️ **A fixed 0.16 was the first attempt and it was too tight** — four pieces
   * within 16 cm of the centre pile up instead of crossing. ⚠️ **A fixed 0.3 was
   * the second and it was too loose**: a test caught a piece 6 cm inside the
   * next cell. Deriving it gets the spread without the overhang.
   *
   * ⚠️ **Ignore any older note claiming a tight heap "reads as a standing
   * stump".** It does not — that was the isometric preview drawing a piece lying
   * on its diagonal as a vertical line, and `Design/samples/53`'s plan view
   * disproves it.
   */
  /** How high a piece after the first rides above the ground, in metres. */
  maxLift: 0.07,
  /**
   * 🔴 **How far a piece may reach OUTSIDE its own cell, in metres.**
   *
   * 🎯 **haugur, 2026-09-02:** *"yes let them overhang the cell."*
   *
   * ⚠️ **Bounded rather than free, and the bound is what the renderer can
   * promise.** A chunk draws the pieces it owns plus a margin around itself —
   * the same trick birch already uses for a crown that hangs over its
   * neighbours — and a piece that reached further than that margin would be
   * **clipped away at a chunk boundary and reappear as you walked**. 0.6 m keeps
   * a 1.5 m piece inside one cell of margin whichever way it points.
   */
  maxOverhang: 0.6,
});

/** `a × b`. */
function cross(a: readonly number[], b: readonly number[]): [number, number, number] {
  return [
    a[1]! * b[2]! - a[2]! * b[1]!,
    a[2]! * b[0]! - a[0]! * b[2]!,
    a[0]! * b[1]! - a[1]! * b[0]!,
  ];
}

/** `a / |a|`, or the axis itself if it is degenerate. */
function unit(a: readonly number[]): [number, number, number] {
  const len = Math.hypot(a[0]!, a[1]!, a[2]!) || 1;
  return [a[0]! / len, a[1]! / len, a[2]! / len];
}

/**
 * Build one piece of driftwood, **in coordinates local to its own cell**.
 *
 * The piece is a chain of {@link SEGMENTS} prisms with {@link SIDES} sides,
 * bowed along its length, tapered from one end to the other, and closed with a
 * fan at each end.
 *
 * 🔴 **LOCAL, NOT WORLD, AND A TEST FORCED THAT CHANGE.** The first version
 * returned world coordinates and the winding test failed at 0.95 where it wanted
 * 1.0 — **not a winding fault at all, but `Float32Array` running out of
 * precision.** The world box sits at ISN93 easting ~347,580, and a 32-bit float
 * there is spaced about **3 cm apart** — so a normal recomputed from stored
 * corners a few centimetres apart came back pointing somewhere else entirely.
 * ⚠️ **A driftwood piece has 5 cm features. It would have been visibly mangled,
 * and only in the real world box** — every test at small coordinates would have
 * passed. ✅ `torchmesh.ts` already works chunk-relative for the same reason.
 * **The caller adds the cell's position.**
 *
 * 🔴 **It rests ON the ground rather than in it**, and that is measured rather
 * than assumed: the piece is built, then lifted so its **lowest vertex** sits at
 * `lift`. Lifting the axis by the radius was the first attempt and it is wrong —
 * a tilted piece dips one end below its own centre, and it sank 2.8 cm into the
 * shore.
 *
 * ⚠️ **AND IT MAY NOW REACH PAST ITS OWN CELL.** 🎯 **haugur, 2026-09-02:**
 * *"yes let them overhang the cell."* 🔴 **The cost was put to him and he took
 * it: the ends of a long piece are timber you can SEE and cannot AIM AT**, because
 * the block occupies one cell however far the drawing spills out of it. **Aim at
 * the middle.**
 *
 * ✅ **The overhang is bounded rather than unlimited** — {@link SHAPE.maxOverhang}
 * — so a piece can lie across its neighbours without reaching a cell that is not
 * next door, and a chunk knows how far outside itself to look. **Birch already
 * overhangs its cell** and the mesher scans a 1 m margin for exactly this.
 */
export function buildDriftPiece(at: DriftAt, index = 0): DriftGeometry {
  /**
   * ⚠️ **Every salt is shifted by the piece's index**, so the second piece in a
   * cell is not the first one wearing a different hat. Without this a heap is
   * several copies of one stick sitting on top of each other.
   */
  const s = index * 977;
  const length = pick(at, s + 1, SHAPE.minLength, SHAPE.maxLength);
  /* 🔴 DERIVED from length, never rolled beside it — see SHAPE.minAspect. */
  const radius = length / pick(at, s + 2, SHAPE.minAspect, SHAPE.maxAspect) / 2;
  const taper = pick(at, s + 3, SHAPE.minTaper, SHAPE.maxTaper);
  const yaw = pick(at, s + 4, 0, Math.PI * 2);
  const tilt = pick(at, s + 5, -SHAPE.maxTilt, SHAPE.maxTilt);
  const bow = pick(at, s + 6, -SHAPE.maxBow, SHAPE.maxBow) * length;
  /* Which way the bow bends, independent of which way the piece points. */
  const bowRoll = pick(at, s + 7, 0, Math.PI * 2);

  /**
   * Where in the cell this piece lies, and how high it rides.
   *
   * ⚠️ **Pieces after the first sit a little higher**, so a heap looks stacked
   * rather than interpenetrating at one level. It is a small lift — driftwood
   * lies down, it does not stand in a pyramid.
   */
  const lift = index === 0 ? 0 : pick(at, s + 10, 0, SHAPE.maxLift);

  /* The axis, and two directions across it. */
  const n: [number, number, number] = [
    Math.cos(yaw) * Math.cos(tilt),
    Math.sin(tilt),
    Math.sin(yaw) * Math.cos(tilt),
  ];
  /* 🔴 The helper must not be parallel to the axis or the cross product is zero
     and every corner collapses onto the centre line — torchmesh.ts's trap. A
     piece lies nearly flat, so world up is safe, but the guard costs nothing. */
  const helper: readonly number[] = Math.abs(n[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const u = unit(cross(n, helper));
  const v = unit(cross(n, u));

  /**
   * 🔴 **How far this piece may wander from the middle of its cell.**
   *
   * Derived from how far it actually REACHES along each axis — half its length
   * projected onto that axis, plus its thickness and its bow — and measured
   * against the cell **plus its allowed overhang**, not against the cell alone.
   *
   * ⚠️ **Before haugur lifted the limit this clamped to the bare metre**, which
   * meant a long piece had nowhere to sit but the middle and a heap of them
   * crossed at one point. Now a long piece may lie across the boundary, and the
   * clamp only stops it reaching a cell that is not even adjacent.
   */
  const room = (axis: number): number =>
    Math.max(0, 0.5 + SHAPE.maxOverhang - (Math.abs(axis) * length) / 2 - radius - Math.abs(bow));
  const offU = pick(at, s + 8, -1, 1) * room(n[0]);
  const offV = pick(at, s + 9, -1, 1) * room(n[2]);

  /* The centre of the piece, in cell-local metres. Y is settled after building. */
  const cx = 0.5 + offU;
  const cy = radius;
  const cz = 0.5 + offV;

  /* The ring centres and radii along the axis. */
  const rings: { c: [number, number, number]; corners: [number, number, number][] }[] = [];
  for (let s = 0; s <= SEGMENTS; s++) {
    const t = s / SEGMENTS;
    /* -0.5 … +0.5 along the axis. */
    const along = (t - 0.5) * length;
    /* A parabola, zero at both ends and `bow` in the middle. */
    const off = bow * (1 - (2 * t - 1) ** 2);
    const bu = Math.cos(bowRoll) * off;
    const bv = Math.sin(bowRoll) * off;

    const c: [number, number, number] = [
      cx + n[0] * along + u[0] * bu + v[0] * bv,
      cy + n[1] * along + u[1] * bu + v[1] * bv,
      cz + n[2] * along + u[2] * bu + v[2] * bv,
    ];

    /* Taper down the length, then wobble each side so it is not a dowel. */
    /**
     * ⚠️ **The corners are worked out here rather than looked up later.**
     *
     * `map` and `at`, never an indexed loop — indexing an array with a variable
     * is the object-injection pattern the linter flags, and building the value
     * where it is defined answers the question instead of suppressing it. Same
     * reasoning as the Maps in `blocks.ts` and the `map` in `hearth.ts`.
     */
    const base = radius * (1 - t * (1 - taper));
    const corners = Array.from({ length: SIDES }, (_, k): [number, number, number] => {
      const wob = base * (1 + pick(at, 20 + s * SIDES + k, -SHAPE.maxWobble, SHAPE.maxWobble));
      const ang = (k / SIDES) * Math.PI * 2;
      const du = Math.cos(ang) * wob;
      const dv = Math.sin(ang) * wob;
      return [
        c[0] + u[0] * du + v[0] * dv,
        c[1] + u[1] * du + v[1] * dv,
        c[2] + u[2] * du + v[2] * dv,
      ];
    });
    rings.push({ c, corners });
  }

  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  /**
   * Add one flat-shaded triangle.
   *
   * ⚠️ **The normal is computed FROM THE CORNERS**, never passed in. That is
   * the whole lesson of torchmesh.ts's two backwards faces: a listed normal can
   * disagree with the winding beside it, and a derived one cannot.
   */
  const tri = (
    a: readonly [number, number, number],
    b: readonly [number, number, number],
    c: readonly [number, number, number],
  ): void => {
    const nx = unit(
      cross([b[0] - a[0], b[1] - a[1], b[2] - a[2]], [c[0] - a[0], c[1] - a[1], c[2] - a[2]]),
    );
    const base = positions.length / 3;
    for (const p of [a, b, c]) {
      positions.push(p[0], p[1], p[2]);
      normals.push(nx[0], nx[1], nx[2]);
    }
    indices.push(base, base + 1, base + 2);
  };

  /* The barrel. Each quad is two triangles, wound so the normal points OUT. */
  for (const [s, ring] of rings.entries()) {
    const next = rings.at(s + 1);
    if (next === undefined) break;
    for (const [k, a] of ring.corners.entries()) {
      const k2 = (k + 1) % SIDES;
      const b = ring.corners.at(k2)!;
      const c = next.corners.at(k2)!;
      const d = next.corners.at(k)!;
      tri(a, b, c);
      tri(a, c, d);
    }
  }

  /* The two ends, each a fan from its ring's centre. */
  for (const { ring, flip } of [
    { ring: rings.at(0)!, flip: true },
    { ring: rings.at(-1)!, flip: false },
  ]) {
    for (const [k, a] of ring.corners.entries()) {
      const b = ring.corners.at((k + 1) % SIDES)!;
      /* The near end faces the other way, so its fan is wound the other way. */
      if (flip) tri(ring.c, b, a);
      else tri(ring.c, a, b);
    }
  }

  /**
   * 🔴 **Settle the height by MEASURING, not by arithmetic.**
   *
   * The lowest vertex goes to `lift`. Working the drop out from the tilt and the
   * bow would be a second formula that has to agree with the first one for ever;
   * reading it off the geometry that was actually built cannot disagree with it.
   */
  const lowest = positions.reduce((lo, p, i) => (i % 3 === 1 ? Math.min(lo, p) : lo), Infinity);
  const rise = lift - lowest;
  /* `reduce` and `map`, never an indexed write — the same object-injection rule
     the corners follow, and the same one `blocks.ts` and `hearth.ts` follow. */
  const settled = positions.map((p, i) => (i % 3 === 1 ? p + rise : p));

  return {
    positions: new Float32Array(settled),
    normals: new Float32Array(normals),
    indices: new Uint32Array(indices),
  };
}
